import { useState, useEffect } from 'react';
import { Search, Bell, Download, Plus, X } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AuditRun } from '../../types';
import { Tooltip } from '../shared';

interface Competitor {
  name: string;
  score: number;
}

/**
 * Competitive Intelligence Screen
 */
export function CompetitiveIntel() {
  const { user } = useAuth();
  const [latestAudit, setLatestAudit] = useState<AuditRun | null>(null);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load latest audit
      const auditsQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(1)
      );
      const auditsSnapshot = await getDocs(auditsQuery);
      if (!auditsSnapshot.empty) {
        setLatestAudit(auditsSnapshot.docs[0].data() as AuditRun);
      }

      // Load competitors from Firebase
      const competitorsDoc = await getDoc(doc(db, 'competitorSettings', user.uid));
      if (competitorsDoc.exists()) {
        const data = competitorsDoc.data();
        setCompetitors(data.competitors || []);
      }
    } catch (error) {
      console.error('Error loading competitive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCompetitor = async () => {
    if (!newCompetitorName.trim() || !user) return;

    const mockScore = Math.floor(Math.random() * 40) + 40; // Random 40-80
    const updatedCompetitors = [...competitors, { name: newCompetitorName.trim(), score: mockScore }];

    try {
      await setDoc(doc(db, 'competitorSettings', user.uid), {
        userId: user.uid,
        competitors: updatedCompetitors,
        updatedAt: new Date()
      });
      setCompetitors(updatedCompetitors);
      setNewCompetitorName('');
      setShowAddCompetitor(false);
    } catch (error) {
      console.error('Error adding competitor:', error);
      alert('Failed to add competitor');
    }
  };

  const removeCompetitor = async (index: number) => {
    if (!user) return;

    const updatedCompetitors = competitors.filter((_, i) => i !== index);

    try {
      await setDoc(doc(db, 'competitorSettings', user.uid), {
        userId: user.uid,
        competitors: updatedCompetitors,
        updatedAt: new Date()
      });
      setCompetitors(updatedCompetitors);
    } catch (error) {
      console.error('Error removing competitor:', error);
      alert('Failed to remove competitor');
    }
  };

  const yourScore = latestAudit?.visibilityScore || 0;
  const allScores = [{ name: 'You', score: yourScore, isYou: true }, ...competitors.map(c => ({ ...c, isYou: false }))];
  const maxScore = Math.max(...allScores.map(s => s.score), 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Competitive</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
            <h1 className="text-3xl font-semibold text-gray-900">Competitive Benchmark</h1>
            <p className="text-sm text-gray-600 mt-1">Compare your AI visibility against competitors in your category</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddCompetitor(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="w-4 h-4" />
              Add competitor
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-t1-primary rounded-lg text-sm font-semibold text-white hover:bg-t1-dark">
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Add Competitor Modal */}
        {showAddCompetitor && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newCompetitorName}
                onChange={(e) => setNewCompetitorName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCompetitor()}
                placeholder="Competitor name"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t1-primary"
                autoFocus
              />
              <button
                onClick={addCompetitor}
                className="px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddCompetitor(false);
                  setNewCompetitorName('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Score Comparison Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {allScores.slice(0, 4).map((item, index) => (
            <div
              key={index}
              className={`bg-white rounded-lg border-2 ${
                item.isYou ? 'border-t1-primary' : 'border-gray-200'
              } shadow-sm p-6 relative`}
            >
              {!item.isYou && (
                <button
                  onClick={() => removeCompetitor(index - 1)}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <p className="text-sm font-medium text-gray-600 mb-2">{item.name}</p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-5xl font-bold ${item.isYou ? 'text-t1-primary' : 'text-gray-900'}`}>
                  {item.score}
                </span>
                <span className="text-xl text-gray-400">/100</span>
              </div>
              {item.isYou && (
                <p className="text-xs text-t1-primary font-semibold">Your Score</p>
              )}
            </div>
          ))}
        </div>

        {/* Recommendation Share Chart */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Recommendation Share</h2>
          {allScores.length > 0 ? (
            <div className="space-y-4">
              {allScores.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {item.name}
                      {item.isYou && <span className="ml-2 text-xs text-t1-primary">(You)</span>}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{item.score}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${item.isYou ? 'bg-t1-primary' : 'bg-gray-400'}`}
                      style={{ width: `${(item.score / maxScore) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Tooltip content="Add competitors to compare recommendation share across AI platforms">
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No competitor data</p>
                <p className="text-xs mt-1">Add competitors above to see comparisons</p>
              </div>
            </Tooltip>
          )}
        </div>

        {/* Insights */}
        {competitors.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Insights</h3>
            <ul className="space-y-1 text-sm text-blue-800">
              <li>
                • Your score is {yourScore > Math.max(...competitors.map(c => c.score)) ? 'higher' : 'lower'} than your top competitor
              </li>
              <li>
                • Industry average is typically 60-70 for established brands
              </li>
              <li>
                • Competitor data is tracked weekly to identify trends
              </li>
            </ul>
          </div>
        )}

        {competitors.length === 0 && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <p className="text-gray-600 mb-4">No competitors configured yet</p>
            <button
              onClick={() => setShowAddCompetitor(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark"
            >
              <Plus className="w-4 h-4" />
              Add your first competitor
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
